import type { ActionFunctionArgs, LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { json, redirect } from "@remix-run/cloudflare";
import { Form, useActionData, useNavigation, useSearchParams } from "@remix-run/react";
import { eq } from "drizzle-orm";
import { z } from "zod";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { logAudit, getRequestIp } from "~/lib/audit.server";
import { verifyPassword } from "~/lib/auth/crypto.server";
import { buildSessionCookie, createSession, readSessionFromRequest } from "~/lib/auth/sessions.server";
import { getDb, schema } from "~/lib/db/client";
import { checkRateLimit, resetRateLimit } from "~/lib/kv/rate-limit";
import { envOf } from "~/load-context";

export const meta: MetaFunction = () => [{ title: "تسجيل الدخول — بنك الأسلحة" }];

const LoginSchema = z.object({
  // Historically called "email" — the column is still `email` in D1 — but
  // operators log in with a short handle (e.g. "shhab"), not necessarily
  // an email. Validate as a generic identifier.
  email: z
    .string()
    .min(1, "اسم المستخدم مطلوب")
    .max(255)
    .regex(/^[^\s]+$/, "اسم المستخدم لا يحتوي مسافات"),
  password: z.string().min(1, "كلمة المرور مطلوبة").max(255),
  // FormData.get() returns `null` for missing fields; Zod's .optional() only
  // accepts `undefined`, so we coerce null/empty to undefined here.
  next: z
    .preprocess((v) => (v === null || v === "" ? undefined : v), z.string().optional())
    .optional(),
});

export async function loader({ request, context }: LoaderFunctionArgs) {
  const env = envOf(context);
  const { rec } = await readSessionFromRequest(env, request);
  if (rec) {
    return redirect(rec.aal === 2 ? "/" : "/verify-2fa");
  }
  return json({});
}

export async function action({ request, context }: ActionFunctionArgs) {
  try {
    return await loginAction(request, context);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[login.action] unhandled:", err);
    const message = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
    return json({ error: `LoginError: ${message}` }, { status: 500 });
  }
}

async function loginAction(request: Request, context: ActionFunctionArgs["context"]) {
  const env = envOf(context);
  const ip = getRequestIp(request) ?? "unknown";
  const rlMax = Number(env.RATE_LIMIT_LOGIN_MAX) || 5;
  const rlWindow = Number(env.RATE_LIMIT_LOGIN_WINDOW) || 900;

  const ok = await checkRateLimit(env, `login:${ip}`, rlMax, rlWindow);
  if (!ok) {
    return json(
      { error: "تجاوزت عدد المحاولات المسموح بها. حاول لاحقاً." },
      { status: 429 },
    );
  }

  const formData = await request.formData();
  const parsed = LoginSchema.safeParse({
    email: formData.get("email") ?? "",
    password: formData.get("password") ?? "",
    next: formData.get("next"),
  });
  if (!parsed.success) {
    return json(
      { error: parsed.error.issues[0]?.message ?? "البيانات المدخلة غير صحيحة" },
      { status: 400 },
    );
  }

  const db = getDb(context);
  const [user] = await db
    .select()
    .from(schema.users)
    .where(eq(schema.users.email, parsed.data.email.toLowerCase()))
    .limit(1);

  // Identical error for "no user" vs "wrong password" to prevent email enumeration.
  if (!user || !(await verifyPassword(parsed.data.password, user.passwordHash))) {
    await logAudit(context, {
      action: "LOGIN_FAILED",
      userEmail: parsed.data.email,
      ip,
      details: user ? "wrong password" : "unknown email",
    });
    return json({ error: "اسم المستخدم أو كلمة المرور غير صحيحة" }, { status: 401 });
  }

  if (!user.isActive) {
    await logAudit(context, {
      action: "LOGIN_FAILED",
      userId: user.id,
      userEmail: user.email,
      ip,
      details: "account disabled",
    });
    return json({ error: "تم تعطيل الحساب — راجع المسؤول" }, { status: 403 });
  }

  if (user.lockedUntil && user.lockedUntil > Math.floor(Date.now() / 1000)) {
    return json({ error: "الحساب مقفل مؤقتاً — حاول لاحقاً" }, { status: 423 });
  }

  // Issue an aal=1 session. If TOTP is enabled, the next step at /verify-2fa
  // will upgrade aal to 2. If TOTP is NOT enabled, /setup-2fa will require it.
  const sid = await createSession(env, {
    userId: user.id,
    role: user.role,
    aal: 1,
    ip,
    userAgent: request.headers.get("User-Agent"),
  });

  // last-login bookkeeping is best-effort — never block auth on a D1 hiccup.
  try {
    await db
      .update(schema.users)
      .set({ lastLoginAt: Math.floor(Date.now() / 1000), lastLoginIp: ip, failedLogins: 0 })
      .where(eq(schema.users.id, user.id));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[login] last-login update failed (non-fatal):", err);
  }

  await resetRateLimit(env, `login:${ip}`);
  await logAudit(context, {
    action: "LOGIN",
    userId: user.id,
    userEmail: user.email,
    ip,
    userAgent: request.headers.get("User-Agent"),
  });

  const cookie = buildSessionCookie(env);
  const headers = new Headers();
  headers.append("Set-Cookie", await cookie.serialize(sid));

  const next = parsed.data.next && parsed.data.next.startsWith("/") ? parsed.data.next : "/";
  const dest = user.totpEnabled ? `/verify-2fa?next=${encodeURIComponent(next)}` : "/setup-2fa";
  return redirect(dest, { headers });
}

export default function LoginPage() {
  const actionData = useActionData<typeof action>();
  const nav = useNavigation();
  const [params] = useSearchParams();
  const next = params.get("next") ?? "";
  const isSubmitting = nav.state === "submitting";

  return (
    <main className="grid min-h-dvh place-items-center p-6 bg-paper-100">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto size-14 rounded-xl bg-primary-500 grid place-items-center text-white mb-3">
            <svg viewBox="0 0 24 24" className="size-7" fill="currentColor">
              <path d="M12 2 4 6v6c0 5 3.5 9.5 8 10 4.5-.5 8-5 8-10V6l-8-4z" />
            </svg>
          </div>
          <CardTitle className="text-xl">تسجيل الدخول</CardTitle>
          <CardDescription>بنك المعلومات العراقي للأسلحة</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {actionData && "error" in actionData ? (
            <Alert variant="destructive">
              <AlertDescription>{actionData.error}</AlertDescription>
            </Alert>
          ) : null}

          <Form method="post" className="space-y-4">
            <input type="hidden" name="next" value={next} />

            <div className="space-y-2">
              <Label htmlFor="email">اسم المستخدم</Label>
              <Input
                id="email"
                name="email"
                type="text"
                autoComplete="username"
                dir="ltr"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">كلمة المرور</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                dir="ltr"
                required
                disabled={isSubmitting}
              />
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "جاري التحقق..." : "تسجيل الدخول"}
            </Button>
          </Form>

          <div className="text-xs pt-2">
            <a href="/forgot" className="text-primary-600 hover:underline">
              نسيت كلمة المرور؟
            </a>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}
